// React imports
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

//  material-ui imports
import { Stack, TextField, Typography } from '@mui/material';

// project imports
import gestopAPI from 'utils/gestopAPI';
import FileMngrDialog from './FileMngrDialog';
import { isSystemFolder } from '..';
import { getFileMngrErrorMessage, isFileMngrResponseSuccessful } from '../utils/fileMngrPath';

// third-party imports
import { useIntl } from 'react-intl';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

// ==============================|| FILEMNGR DIALOG NEW FOLDER ||============================== //

/**
 * FileMngrDialogNewFolder component - Responsible for creating a new folder in the file manager dialog.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {boolean} props.openNewFolderDialog - Flag to control the visibility of the new folder dialog.
 * @param {Function} props.handleCloseNewFolderDialog - Callback function to handle the close event of the new folder dialog.
 * @param {Function} props.handleRefreshFolder - Callback function to handle the refresh event of the folder.
 * @param {string} props.currentPath - The current path of the folder.
 * @param {string} props.url - The URL for the API.
 * @returns {JSX.Element} The JSX element representing the new folder dialog component.
 */
const FileMngrDialogNewFolder = ({ openNewFolderDialog, handleCloseNewFolderDialog, handleRefreshFolder, currentPath, url }) => {
    const [newFolderName, setNewFolderName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const intl = useIntl();
    const dispatch = useDispatch();

    const handleCreateNewFolder = async () => {
        if (newFolderName !== '') {
            try {
                setIsLoading(true);

                const path = currentPath;
                const action = 'create';
                const data = {};
                const name = newFolderName;

                const response = await gestopAPI.post(url, {
                    action,
                    data,
                    name,
                    path
                });

                if (isFileMngrResponseSuccessful(response)) {
                    await handleRefreshFolder();
                    handleCloseNewFolderDialog();
                } else {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: getFileMngrErrorMessage(response) || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
                }
            } catch (error) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message:
                            error?.message ||
                            intl.formatMessage({ id: 'ui-comp-filemngr-dialog-createnewfolder-errmsg-errcreatingfolder' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
            } finally {
                setIsLoading(false);
            }
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-createnewfolder-errmsg-nofoldername' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    useEffect(() => {
        setNewFolderName('');
    }, [openNewFolderDialog]);

    return (
        <FileMngrDialog
            title={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-createnewfolder-title' })}
            isOpen={openNewFolderDialog}
            onClose={() => {
                if (typeof handleCloseNewFolderDialog === 'function') handleCloseNewFolderDialog();
                setNewFolderName('');
            }}
            buttons={
                newFolderName.trim() !== ''
                    ? [
                          {
                              text: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-button-create' }),
                              onClick: handleCreateNewFolder,
                              variant: 'contained',
                              color: 'warning',
                              disabled: isLoading || isSystemFolder(newFolderName.trim()),
                              isLoading
                          }
                      ]
                    : []
            }
        >
            <Stack spacing={1.5} justifyContent="center" alignItems="center" width={320} p={1}>
                <TextField
                    label={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-createnewfolder-textfield-label' })}
                    value={newFolderName.trim()}
                    onChange={(e) => setNewFolderName(e.target.value.trim())}
                    variant="outlined"
                    fullWidth
                    focused
                />
                <Typography color="error" sx={{ display: isSystemFolder(newFolderName.trim()) ? 'block' : 'none' }}>
                    {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-systemfolder' })}
                </Typography>
            </Stack>
        </FileMngrDialog>
    );
};

FileMngrDialogNewFolder.propTypes = {
    openNewFolderDialog: PropTypes.bool,
    handleCloseNewFolderDialog: PropTypes.func,
    handleRefreshFolder: PropTypes.func,
    currentPath: PropTypes.string,
    url: PropTypes.string
};

export default FileMngrDialogNewFolder;
